/**
 * AppStoreBadges — v147.99.47 (Feb 2026)
 *
 * Play Store + App Store badges. Fetches the store URLs from the
 * public `/app-store-links` endpoint (admin-configurable via
 * /admin/app-version) with a hardcoded fallback so we render even
 * if the backend is temporarily unreachable.
 *
 * Two variants:
 *   size="lg" — used under the landing hero CTA (bold, brutalist buttons)
 *   size="sm" — used in the footer (compact, secondary)
 *
 * Both variants render the actual official store SVG artwork instead
 * of generic text buttons so the badges feel legit + on-brand.
 */
import { useEffect, useState } from "react";

// Hardcoded defaults — match `_DEFAULT_ANDROID_URL` / `_DEFAULT_IOS_URL`
// in backend/routers/admin_app_version.py. If admin edits store links,
// the fetch below picks the new values up on next page load.
const DEFAULT_ANDROID = "https://play.google.com/store/apps/details?id=in.localappstore.creatoraiq";
const DEFAULT_IOS = "https://apps.apple.com/app/linkola/id0000000000";

export default function AppStoreBadges({ size = "lg", className = "" }) {
  const [links, setLinks] = useState({ android: DEFAULT_ANDROID, ios: DEFAULT_IOS });

  useEffect(() => {
    let cancelled = false;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/app-store-links`;
    fetch(url, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (!cancelled && data) {
          setLinks({
            android: data.android || DEFAULT_ANDROID,
            ios: data.ios || DEFAULT_IOS,
          });
        }
      })
      .catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, []);

  const box = size === "lg"
    ? "h-12 sm:h-14 px-4 gap-2.5"
    : "h-9 px-3 gap-2 text-xs";

  return (
    <div className={`flex flex-wrap items-center gap-2 sm:gap-3 ${className}`}
         data-testid={`app-store-badges-${size}`}>
      <a
        href={links.android}
        target="_blank" rel="noopener noreferrer"
        data-testid="badge-playstore"
        className={`group inline-flex items-center ${box} border-2 border-ink bg-ink text-white shadow-brutalSm hover:-translate-y-0.5 transition-transform`}
      >
        <PlayStoreIcon className={size === "lg" ? "w-6 h-6" : "w-4 h-4"} />
        <span className="flex flex-col leading-tight text-left">
          <span className={size === "lg" ? "text-[9px] uppercase tracking-widest opacity-80" : "text-[8px] uppercase tracking-widest opacity-80"}>
            Get it on
          </span>
          <span className={size === "lg" ? "font-black text-sm sm:text-base" : "font-black"}>
            Google Play
          </span>
        </span>
      </a>
      <a
        href={links.ios}
        target="_blank" rel="noopener noreferrer"
        data-testid="badge-appstore"
        className={`group inline-flex items-center ${box} border-2 border-ink bg-white text-ink shadow-brutalSm hover:-translate-y-0.5 transition-transform`}
      >
        <AppleIcon className={size === "lg" ? "w-6 h-6" : "w-4 h-4"} />
        <span className="flex flex-col leading-tight text-left">
          <span className={size === "lg" ? "text-[9px] uppercase tracking-widest opacity-70" : "text-[8px] uppercase tracking-widest opacity-70"}>
            Download on the
          </span>
          <span className={size === "lg" ? "font-black text-sm sm:text-base" : "font-black"}>
            App Store
          </span>
        </span>
      </a>
    </div>
  );
}

/* Official-style artwork rendered as inline SVGs so we don't have
   to ship binary badge images. Colors + shape match Google/Apple's
   brand guidelines closely enough for a "clearly the right app"
   recognition without infringing on their exact badge assets. */

function PlayStoreIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      {/* Play triangle body: 4 colored triangles fanning around the origin */}
      <path fill="#00D4FF" d="M3.5 3.2 L14 12 L3.5 20.8 C3.2 20.6 3 20.2 3 19.8 V4.2 C3 3.8 3.2 3.4 3.5 3.2Z"/>
      <path fill="#FFEB3B" d="M17.5 15.3 L14 12 L17.5 8.7 L20.8 10.6 C21.6 11 21.6 13 20.8 13.4 Z"/>
      <path fill="#FF3D00" d="M3.5 3.2 L14 12 L17.5 8.7 L4.6 1.9 C4.2 1.7 3.8 1.8 3.5 2 Z"/>
      <path fill="#00C853" d="M3.5 20.8 L14 12 L17.5 15.3 L4.6 22.1 C4.2 22.3 3.8 22.2 3.5 22 Z"/>
    </svg>
  );
}

function AppleIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
}
